/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback sagas
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.13.1
 * @since 1.12.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { IOptionItemProps } from '../../../components/common/Select/Select';
import { SET_TABLE_FILTER } from '../../../components/Table/constants/actionConstants';
import { TSetTableFilterAction } from '../../../components/Table/types/tableTypes';
import { pushGTMAnalytic } from '../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../modules/analytics/GoogleTagManager/GTMOperations';
import {
    ACCOUNT_CASHBACK_META,
    ACCOUNT_CASHBACK_META_WITH_ID,
    ACCOUNT_CASHBACK_SEARCH,
} from '../../../modules/app/Api';
import { makeSelectPageCashbackTranslation } from '../../../modules/internationalization/intSelectors';
import { IPageCashbackTranslation } from '../../../modules/internationalization/types/pages/cashback';
import { setLocalStorageDataAction } from '../../../modules/storage/storageActions';
import { makeSelectLocalStorageSelectedAccount } from '../../../modules/storage/storageSelectors';
import { ELocalStorageKeys } from '../../../modules/storage/storageTypes';
import { openRoute } from '../../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../../routes/constants/routesConstants';
import { requestSaga } from '../../../sagas/requestSaga';
import { formQueryParamsString } from '../../../sagas/utils/formQueryParamsString';
import { stringFormatParams } from '../../../utils/stringFormat';
import {
    EWidgetSearchAccountsField,
    IWidgetSearchAccountsItem,
} from '../../PageDashboard/components/Widgets/WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import {
    cashbackGetAccountListFailureAction,
    cashbackGetAccountListSuccessAction,
    cashbackGetMetaFailedAction,
    cashbackGetMetaSuccessAction,
} from '../actions/cashbackActions';
import {
    CASHBACK_GET_ACCOUNT_LIST_START,
    CASHBACK_GET_META_START,
    CASHBACK_GET_SERVICE_LIST_START,
    CASHBACK_RESERVE_START,
    CASHBACK_RESERVE_SUCCESS,
    CASHBACK_SET_ACCOUNT,
    CASHBACK_TOGGLE_AUTO_SPENDING_START,
} from '../pageCashbackConstants';
import { makeSelectCashbackClientId, makeSelectCashbackSelectList } from '../selectors/cashbackSelectors';
import { CashbackHistoryTableField } from '../types/cashbackHistoryTypes';
import {
    TCashbackGetAccountListResponse,
    TCashbackGetAccountListStartAction,
    TCashbackGetMetaResponse,
    TCashbackSetAccountAction,
} from '../types/cashbackTypes';
import { cashbackGetProductList, cashbackReserveSaga, cashbackToggleAutoSpendingSaga } from './cashbackControlSaga';

/*
* -------------------------------------------
*         Get Cashback Meta
* -------------------------------------------
*/

export function* cashbackGetMetaSaga(): SagaIterator {
    const accountLocalStorage: IWidgetSearchAccountsItem = yield select(makeSelectLocalStorageSelectedAccount());
    const clientId = yield select(makeSelectCashbackClientId());
    const id = clientId || accountLocalStorage.id;
    try {
        const url = id
            ? stringFormatParams(ACCOUNT_CASHBACK_META_WITH_ID, { clientId: id })
            : ACCOUNT_CASHBACK_META
        ;

        const {
            autoSpendingBonusesEnabled,
            accountInfo,
            ...meta
        }: TCashbackGetMetaResponse = yield call(requestSaga, url, 'GET');
        const accountSelectCurrent = { label: accountInfo.number, value: accountInfo.id };

        yield put(cashbackGetMetaSuccessAction({
            accountSelectCurrent,
            meta: {
                ...meta,
                isAutoSpendingEnabled: autoSpendingBonusesEnabled,
            },
        }));

        // Если перешли с карточки ЛС, то storage менять не нужно
        if (!clientId) {
            accountLocalStorage.id = accountSelectCurrent.value;
            accountLocalStorage[EWidgetSearchAccountsField.number] = accountSelectCurrent.label;
            yield put(setLocalStorageDataAction({
                key: ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT,
                value: accountLocalStorage,
            }));
        }
    } catch (e: any) {
        yield put(cashbackGetMetaFailedAction(e));
    }
}

/*
* -------------------------------------------
*          Cashback Account Select
* -------------------------------------------
*/

export function* cashbackGetAccountListSaga(action: TCashbackGetAccountListStartAction): SagaIterator {
    const accountList: IOptionItemProps[] = yield select(makeSelectCashbackSelectList());
    try {
        const queryString = formQueryParamsString({
            from: action.payload.start ? 0 : accountList.length,
            term: encodeURIComponent(action.payload.searchValue),
            size: 10,
        });
        const url = `${ACCOUNT_CASHBACK_SEARCH}${queryString}`;
        const response: TCashbackGetAccountListResponse = yield call(requestSaga, url, 'GET');
        const loadedList = response.map(account => ({ label: account.number, value: account.id }));
        yield put(cashbackGetAccountListSuccessAction({
            accountSelectList: action.payload.start ? loadedList : [...accountList, ...loadedList],
            accountSelectHasMore: loadedList.length === 10,
        }));
    } catch (e: any) {
        yield put(cashbackGetAccountListFailureAction(e));
    }
}

// при смене ЛС на кешбек, текущий ЛС меняется на такой же на других страницах
export function* cashbackSetAccountSaga({ payload }: TCashbackSetAccountAction) {
    const accountLocalStorage: IWidgetSearchAccountsItem = yield select(makeSelectLocalStorageSelectedAccount());
    accountLocalStorage.id = +(payload.value || 0);
    accountLocalStorage[EWidgetSearchAccountsField.number] = payload.label;

    yield put(setLocalStorageDataAction({
        key: ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT,
        value: accountLocalStorage,
    }));

    openRoute(RoutePath.cashbackAccountId.replace(CLIENT_ID, payload.value.toString()));
}

export function* cashbackSendTableFilterSaga(action: TSetTableFilterAction) {
    if (action.payload.currentFilter) {
        let event = '';
        const translation: IPageCashbackTranslation = yield select(makeSelectPageCashbackTranslation());

        Object.keys(action.payload.currentFilter).forEach((arg: unknown) => {
            if (arg === CashbackHistoryTableField.operationType || arg === CashbackHistoryTableField.service) {
                // @ts-ignore
                const value = (action.payload.currentFilter[arg as CashbackHistoryTableField] as IOptionItemProps[])
                    .map(item => item.value).join();
                event = `${translation.tabHistory.table[arg as CashbackHistoryTableField].header} / ${value}`;
            }
            if (arg === CashbackHistoryTableField.date || arg === CashbackHistoryTableField.scores) {
                event = `${translation.tabHistory.table[arg as CashbackHistoryTableField].header}`;
            }
        });

        yield put(pushGTMAnalytic({
            operation: GTMOperations.cashback.clickOnListsFiltersCashbackPage(event),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        }));
    }
}

/*
* -------------------------------------------
*         Cashback Sagas
* -------------------------------------------
*/

export function* cashbackSagas(): SagaIterator {
    yield all([
        takeEvery(CASHBACK_GET_META_START, cashbackGetMetaSaga),
        takeEvery(CASHBACK_GET_ACCOUNT_LIST_START, cashbackGetAccountListSaga),
        takeEvery(CASHBACK_SET_ACCOUNT, cashbackSetAccountSaga),
        // Control Sagas
        takeEvery(CASHBACK_GET_SERVICE_LIST_START, cashbackGetProductList),
        takeEvery(CASHBACK_RESERVE_START, cashbackReserveSaga),
        takeEvery(CASHBACK_RESERVE_SUCCESS, cashbackGetProductList),
        takeEvery(CASHBACK_TOGGLE_AUTO_SPENDING_START, cashbackToggleAutoSpendingSaga),

        takeEvery(SET_TABLE_FILTER, cashbackSendTableFilterSaga),
    ]);
}
